import React, { useCallback, useState } from 'react'
import { Button, Card, Form, Modal, Table } from 'react-bootstrap'
import callApi from '../../helpers/callAPI'
import useCallApi from '../../hooks/useCallApi'
import AffiliateNavbar from '../../pages/Affiliate/Templates/AffiliateNavbar/AffiliateNavbar'
import Affiliate from '../../templates/Affiliate/Affiliate'
import Campaign from '../../templates/Campaign/Campaign'
import Loading from '../../templates/UI/Loading/Loading'
import ReportTable from '../../templates/ReportTable/ReportTable'

import getDecimalNumber from '../../helpers/getDecimalNumber'
import CampaignNavbar from '../../pages/Campaign/CampaignNavBar/CampaignNavBar'
import SpecialRatesModal from '../SpecialRatesModal/SpecialRatesModal'
import { useAuth, useProtectedPage } from '../../context/AuthContext'
import { useRef } from 'react'
import RateHistoryModal from '../RateHistoryModal/RateHistoryModal'

const SpecialRates = ({ affiliateId, campaignId, showSystemMessage }) => {
    const auth = useAuth()
    useProtectedPage(auth.permissions, 'special_rates')

    const affiliateRateRef = useRef([])
    const networkRateRef = useRef([])
    const displayRateRef = useRef([])
    const displayNetRateRef = useRef([])    
    const displayRateTypeRef = useRef([])
    const overrideUrlRef = useRef([])
    const hideSaleAmountRef = useRef([])

    const [isOpen, setIsOpen] = useState(false)

    const [editAffiliate, setEditAffiliate] = useState(false)
    const [editCampaign, setEditCampaign] = useState(false)
    const [editRateName, setEditRateName] = useState('')
    const [RHisOpen, setRHIsOpen] = useState(false)
    const [selectedRate, setSelectedRate] = useState({ rate_name: '' })

    const { dataLoaded, data, setData, refetch } = useCallApi(affiliateId ? `affiliate/${affiliateId}/specialRates` : `campaign/${campaignId}/specialRates`, 'GET', {}, [affiliateId, campaignId])

    if (dataLoaded && affiliateId) {
        document.title = `${data.affiliate.name}: Special Rates`
    } else if (dataLoaded && campaignId) {
        document.title = `${data.campaign.name}: Special Rates`
    } else {
        document.title = `Special Rates`
    }    

    const openModal = (ratename, affiliate, campaign) => {
        setEditRateName(ratename)
        setEditAffiliate(affiliate)
        setEditCampaign(campaign)
        setIsOpen(true);
    }

    const closeModal = () => {
        setEditRateName('')
        setEditAffiliate(false)
        setEditCampaign(false)
        setIsOpen(false);
    }

    const updateRate = useCallback(
        async (index, affiliateId, campaignId, rateName) => {
            let body = {
                affiliate: {id: affiliateId},
                campaign: {id: campaignId} ,
                rate_name: rateName,
                affiliate_rate: affiliateRateRef.current[index].value,
                network_rate: networkRateRef.current[index].value,
                display_rate: displayRateRef.current[index].value,
                display_net_rate: displayNetRateRef.current[index].value,
                display_rate_type: displayRateTypeRef.current[index].value,
                hide_sale_amount: hideSaleAmountRef.current[index].checked,
                override_url: overrideUrlRef.current[index].value,
            }

            const res = await callApi(affiliateId ? `affiliate/${affiliateId}/specialRates` : `campaign/${campaignId}/specialRates`, 'PUT', body)
            if (res.success) {
                //showSystemMessage('success', 'Rate updated.')
                //This was causing annoying reset issues
                refetch()
            } else {
                showSystemMessage('error', res.errors.message)
            }
        },
        [affiliateId, campaignId, refetch, showSystemMessage, data]
    )

    const deleteRate = useCallback(
        async (index) => {
            const form = { ...data.rates[index], affiliate: data.affiliate, campaign: data.campaign }
            const res = await callApi(affiliateId ? `affiliate/${affiliateId}/specialRates` : `campaign/${campaignId}/specialRates`, 'DELETE', form)
            if (res.success) {
                showSystemMessage('success', 'Rate deleted.')
                refetch()
            } else {
                showSystemMessage('error', res.message)
            }
        },
        [affiliateId, campaignId, refetch, showSystemMessage, data]
    )

    const getReportOutput = () => {
        let columns = [
            {
                Header: 'ID',
                accessor: 'id',
                Cell: ({ cell: { value } }) => {
                    return <>{value}</>
                },
            },
            {
                Header: affiliateId ? 'Campaign' : 'Affiliate',
                accessor: affiliateId ? 'offer.name' : 'affiliate.name',
                Cell: (tableData) => {
                    return <>{affiliateId ? <Campaign oid={tableData.data[tableData.row.index].offer.id} name={tableData.data[tableData.row.index].offer.name} status={tableData.data[tableData.row.index].offer.status} /> : <Affiliate aid={tableData.data[tableData.row.index].affiliate.id} name={tableData.data[tableData.row.index].affiliate.name} status={tableData.data[tableData.row.index].affiliate.status} priority={tableData.data[tableData.row.index].affiliate.priority} />}</>
                },
            },
            {
                Header: 'Status',
                accessor: affiliateId ? 'offer.status' : 'affiliate.status',
                Cell: ({ cell: { value } }) => {
                    return <>{value}</>
                },
            },
            {
                Header: 'Manager',
                accessor: 'manager.name',
                Cell: ({ cell: { value } }) => {
                    return <>{value}</>
                },
            },
            {
                Header: 'Rate Name',
                accessor: 'rate.name',
                Cell: ({ cell: { value }, row: { original } }) => {
                    return <><Button variant="link" onClick={() => {setRHIsOpen(true); setSelectedRate({rate_name: value, affiliate_id: original.affiliate.id})}}>{value}</Button></>
                },
            },
            {
                Header: 'Aff Rate',
                accessor: 'rate.spc_aff_rate',
                Cell: (tableData) => {
                    let spcAffRate = tableData.data[tableData.row.index].rate.spc_aff_rate
                    let currency = tableData.data[tableData.row.index].rate.currency
                    return (
                        <div className="d-flex align-items-center">
                            <Form.Control disabled={tableData.data[tableData.row.index].rate.spc_display_rate > 0 && tableData.data[tableData.row.index].rate.spc_display_net_rate > 0 } type="text" size="sm" ref={(el) => (affiliateRateRef.current[tableData.row.index] = el)} className="w-50" defaultValue={(spcAffRate && spcAffRate > 0) ? spcAffRate : ''} />
                            &nbsp;({tableData.data[tableData.row.index].rate.offer_type === 'F' ? `${currency === 'EUR' ? '€' : '$'}${getDecimalNumber(tableData.data[tableData.row.index].rate.off_aff_rate)}` : `${getDecimalNumber(tableData.data[tableData.row.index].rate.off_aff_rate)}%`})                            
                        </div>
                    )
                },
            },
            {
                Header: 'Net Rate',
                accessor: 'rate.spc_net_rate',
                Cell: (tableData) => {
                    let spcNetRate = tableData.data[tableData.row.index].rate.spc_net_rate
                    let currency = tableData.data[tableData.row.index].rate.currency
                    return (
                        <div className="d-flex align-items-center">
                            <Form.Control disabled={tableData.data[tableData.row.index].rate.spc_display_rate > 0 && tableData.data[tableData.row.index].rate.spc_display_net_rate > 0 } type="text" size="sm" className="w-50" ref={(el) => (networkRateRef.current[tableData.row.index] = el)} defaultValue={(spcNetRate && spcNetRate >= 0) ? spcNetRate : ''} />
                            &nbsp;({tableData.data[tableData.row.index].rate.offer_type === 'F' ? `${currency === 'EUR' ? '€' : '$'}${getDecimalNumber(tableData.data[tableData.row.index].rate.off_net_rate)}` : `${getDecimalNumber(tableData.data[tableData.row.index].rate.off_net_rate)}%`})                            
                        </div>
                    )
                },
            },
            {
                Header: 'Display Rate',
                accessor: 'rate.display_rate',
                Cell: (tableData) => {
                    let displayRate = tableData.data[tableData.row.index].rate.display_rate
                    let spcDisplayRate = tableData.data[tableData.row.index].rate.spc_display_rate
                    let displayRateType = tableData.data[tableData.row.index].rate.display_rate_type
                    let currency = tableData.data[tableData.row.index].rate.currency
                    return (
                        <div className="d-flex align-items-center">
                            <Form.Control type="text" size="sm" className="w-50" ref={(el) => (displayRateRef.current[tableData.row.index] = el)} defaultValue={spcDisplayRate ? spcDisplayRate : ''} />
                            { displayRateType ? <span>&nbsp;({displayRateType === 'F' ? `${currency === 'EUR' ? '€' : '$'}${getDecimalNumber(displayRate)}` : `${getDecimalNumber(displayRate)}%`})</span> : '' }
                        </div>
                    )
                },
            },
            {
                Header: 'Display Net Rate',
                accessor: 'rate.display_net_rate',
                Cell: (tableData) => {
                    let displayNetRate = tableData.data[tableData.row.index].rate.display_net_rate
                    let spcDisplayNetRate = tableData.data[tableData.row.index].rate.spc_display_net_rate
                    let displayRateType = tableData.data[tableData.row.index].rate.display_rate_type
                    let currency = tableData.data[tableData.row.index].rate.currency
                    return (
                        <div className="d-flex align-items-center">
                            <Form.Control type="text" size="sm" className="w-50" ref={(el) => (displayNetRateRef.current[tableData.row.index] = el)} defaultValue={spcDisplayNetRate ? spcDisplayNetRate : ''} />
                            { displayRateType ? <span>&nbsp;({displayRateType === 'F' ? `${currency === 'EUR' ? '€' : '$'}${getDecimalNumber(displayNetRate)}` : `${getDecimalNumber(displayNetRate)}%`})</span> : '' }
                        </div>
                    )
                },
            },
            {
                Header: 'Display Rate Type',
                accessor: 'rate.spc_display_rate_type',
                Cell: (tableData) => {
                    let spcDisplayRateType = tableData.data[tableData.row.index].rate.spc_display_rate_type
                    return (
                        <Form.Control as="select" ref={(el) => (displayRateTypeRef.current[tableData.row.index] = el)} defaultValue={spcDisplayRateType ? spcDisplayRateType : ''}>
                            <option value=""></option>
                            <option value="F">$ per lead</option>
                            <option value="P">% of sale</option>
                            <option value="N">% of commission</option>
                        </Form.Control>
                    )
                },
            },
            {
                Header: <div style={{ textDecoration: 'line-through' }}>Sale</div>,
                accessor: 'rate.spc_hide_sale_amount',
                Cell: (tableData) => {
                    let spcHideSaleAmount = tableData.data[tableData.row.index].rate.spc_hide_sale_amount

                    return <Form.Check type="checkbox" ref={(el) => (hideSaleAmountRef.current[tableData.row.index] = el)} defaultChecked={spcHideSaleAmount} />
                },
            },
            {
                Header: 'Override URL',
                accessor: 'rate.override_url',
                Cell: (tableData) => {
                    let overrideUrl = tableData.data[tableData.row.index].override_url
                    return <Form.Control type="text" size="sm" ref={(el) => (overrideUrlRef.current[tableData.row.index] = el)} defaultValue={overrideUrl || ''} />
                },
            },
            {
                Header: '',
                accessor: 'rate',
                Cell: (tableData) => {
                    return (
                        <div className="d-flex">
                            <Button className="m-1" size="sm" onClick={() => updateRate(tableData.row.index, affiliateId ? affiliateId : tableData.data[tableData.row.index].affiliate.id, campaignId ? campaignId : tableData.data[tableData.row.index].offer.id, tableData.data[tableData.row.index].rate.name)}>
                                Save
                            </Button>
                            <Button variant="warning" size="sm" onClick={() => {openModal(tableData.data[tableData.row.index].rate.name, tableData.data[tableData.row.index].affiliate ? tableData.data[tableData.row.index].affiliate : data.affiliate , tableData.data[tableData.row.index].offer ? tableData.data[tableData.row.index].offer : data.campaign  )}} className="m-1">
                                Edit
                            </Button>
                            <Button variant="danger" size="sm" onClick={() => deleteRate(tableData.row.index)} className="m-1">
                                Delete
                            </Button>
                        </div>
                    )
                },
            },
        ]

        return (
            <React.Fragment>
            <RateHistoryModal specialRates={true} isOpen={RHisOpen} onClose={() => setRHIsOpen(false)} rateName={selectedRate.rate_name} campaignId={campaignId} affiliateId={selectedRate.affiliate_id} />
                <ReportTable
                    data={data.rates}
                    columns={columns}
                    getRowProps={(row) => {
                        let classes = ``

                        if (parseFloat(row.original.rate.spc_net_rate) > 0) {
                            if (parseFloat(row.original.rate.spc_aff_rate) > parseFloat(row.original.rate.spc_net_rate)) {
                                if (row.original.rate.actual_offer_type !== 'A') {
                                    classes = 'bgDanger25'
                                }
                            }
                        } else {
                            if (parseFloat(row.original.rate.spc_aff_rate) > parseFloat(row.original.rate.off_net_rate)) {
                                if (row.original.rate.actual_offer_type !== 'A') {
                                    classes = 'bgDanger25'
                                }
                            }
                        }
                        return {
                            className: classes,
                        }
                    }}
                />
            </React.Fragment>
        )
    }

    return (
        <div>
            <div className="page-header d-flex align-items-center">
                <h1 className="mb-h1">Special Rates</h1>
                <div className="ml-5">{affiliateId ? <AffiliateNavbar affiliateId={affiliateId} showSystemMessage={showSystemMessage} /> : <CampaignNavbar campaignId={campaignId} showSystemMessage={showSystemMessage} />}</div>
            </div>
            <div className="row">
                <div className="col-lg-12">
                    <Card>
                        {
                            dataLoaded &&
                            <Modal show={isOpen} onHide={() => closeModal()}>
                                <SpecialRatesModal setIsOpen={setIsOpen} affiliate={editAffiliate} campaign={editCampaign} ratename={editRateName} refetch={refetch} showSystemMessage={showSystemMessage} />
                            </Modal>
                        }                        
                        <Card.Body className="p-0">
                            <div className="d-flex justify-content-between p-3 align-items-center">
                                <h1 className="mb-h1-dark mr-2">{dataLoaded ? affiliateId ? <Affiliate aid={affiliateId} name={data.affiliate.name} priority={data.affiliate.priority.value} status={data.affiliate.status.value} /> : <Campaign oid={campaignId} name={data.campaign.name} /> : null}</h1>
                                { 
                                    dataLoaded &&
                                        <Button variant="warning" size="sm" onClick={() => openModal('', data.affiliate ? data.affiliate : false, data.campaign ? data.campaign : false)}>
                                            Add Special Rate
                                        </Button>
                                }
                            </div>
                            {dataLoaded ? data.rates.length === 0 ? <div className="p-3">No stats for the current parameters.</div> : getReportOutput() : <Loading></Loading>}
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    )
}

export default SpecialRates
